import { familyLook } from "./accent.js";
import { parseHex } from "./hex.js";

/**
 * Colour for things that are PAINTED rather than styled.
 *
 * **This is not a design system, and there is no palette in it.** The palette is `app.css`; every
 * value here is read back out of it at runtime. `theme.ts` was the old source and is gone — the
 * direction is inverted, so a colour is declared once as CSS, Tailwind builds the utilities, and
 * this file exists purely for the callers that cannot use a class.
 *
 * There are exactly two of those, and both draw rather than style:
 *
 *   - **The game canvas.** Pixi renders with `graphics.fill({ color })`. There is no stylesheet
 *     involved and no class to apply, so a colour has to reach it as a value.
 *   - **The creatures.** They are inline SVG, and `fill` is an attribute rather than a property —
 *     a gradient stop cannot be a utility class.
 *
 * Everything else in the document uses Tailwind and imports nothing from here.
 *
 * The colour MATHS is not data and never was: `mix`, `lighten` and `darken` are functions of
 * whatever they are handed, so they cannot drift from the stylesheet. They stay because the canvas
 * mixes at draw time — often against an animation's progress — and a computed blend has no static
 * form.
 */

/**
 * Every colour token, by name.
 *
 * Listed rather than discovered, and that is the safety property: a name here that `app.css` does
 * not define fails loudly on first use, and a colour added to the CSS but not to this list is
 * simply unavailable to the canvas rather than silently undefined.
 */
export const COLOR_NAMES = [
  "cream",
  "card",
  "ink",
  "muted",
  "edge",
  "sunny",
  "watermelon",
  "turquoise",
  "grape",
  "mint",
  "sky",
  "correct",
  "wrong",
  "stage",
  "stageDeep",
] as const;

export type ColorName = (typeof COLOR_NAMES)[number];

/** `stageDeep` → `--color-stage-deep`. */
const cssVarFor = (name: ColorName): string =>
  `--color-${name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;

const resolved = new Map<string, number>();

/**
 * Reads a custom property off the document root and parses it to a number.
 *
 * **Throws rather than falling back.** A default here would be a wrong colour drawn on a child's
 * screen with nothing anywhere to say so — and since the canvas is the only caller, "wrong colour"
 * can mean a shape invisible against its own background. A missing token is a build mistake and
 * should read as one.
 *
 * Memoised, because this is called from draw code and `getComputedStyle` forces a style
 * recalculation. The palette does not change at runtime — there is one theme, deliberately (a
 * children's product that dims itself at night is one a child cannot recognise).
 */
function readColor(name: ColorName): number {
  const variable = cssVarFor(name);
  const cached = resolved.get(variable);
  if (cached !== undefined) return cached;

  const raw = getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  const value = parseHex(raw);
  if (value === null) {
    throw new Error(
      `design token ${variable} did not resolve to a hex colour (got "${raw}"). An empty value means ` +
        "it is not in the stylesheet at all; anything else means it is there in a form this cannot " +
        "read. It is declared in src/app.css under `@theme static`.",
    );
  }
  resolved.set(variable, value);
  return value;
}

/**
 * Checks every token once, at boot, and reports all of them together.
 *
 * **Called from `main.tsx` before the first render, and that placement is the point.** The first
 * version resolved lazily and threw from inside a draw call, so a token Tailwind had tree-shaken
 * out of `:root` took down a game a child was in the middle of — a fault in the stylesheet
 * surfacing as a crash at the worst possible moment. Failing at boot turns the same fault into a
 * blank screen before anyone has started, which is a bug report rather than a lost session.
 *
 * (Tree-shaking was that first cause: `@theme` emits only the variables some utility references, and
 * the canvas-only colours are referenced by no class, so `app.css` uses `@theme static`.)
 *
 * **This reports what each token actually said, rather than naming a cause.** The version that
 * summarised every failure as "missing from the stylesheet, add `static`" cost an afternoon the day a
 * token was present and merely unparseable: the message described a stylesheet nobody was looking at,
 * and the real value never appeared. Each underlying message carries its own raw value now.
 */
export function assertPaintTokensResolve(): void {
  const problems: string[] = [];
  const record = (error: unknown, fallback: string) =>
    problems.push(error instanceof Error ? error.message : fallback);

  for (const name of COLOR_NAMES) {
    try {
      readColor(name);
    } catch (error) {
      record(error, cssVarFor(name));
    }
  }
  try {
    readSpacing("touch");
  } catch (error) {
    record(error, "--spacing-touch");
  }
  if (problems.length > 0) {
    throw new Error(`design tokens did not resolve:\n- ${problems.join("\n- ")}`);
  }
}

/**
 * The palette as numbers.
 *
 * Lazy getters rather than a plain object, and the laziness is load-bearing: an eagerly built map
 * would run at module-evaluation time, which is before Vite has injected the stylesheet in
 * development — every colour would resolve to empty. By the time anything reads one, the CSS is
 * applied and `assertPaintTokensResolve` has already confirmed it.
 */
export const COLOR = Object.defineProperties(
  {},
  Object.fromEntries(
    COLOR_NAMES.map((name) => [name, { get: () => readColor(name), enumerable: true }]),
  ),
) as Record<ColorName, number>;

/** A spacing token as a number, for canvas layout. Same lookup, same failure mode. */
function readSpacing(name: string): number {
  const variable = `--spacing-${name}`;
  const cached = resolved.get(variable);
  if (cached !== undefined) return cached;

  const raw = getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  const px = /^(\d+(?:\.\d+)?)px$/.exec(raw);
  if (!px?.[1]) {
    throw new Error(`design token ${variable} is missing or not a px value ("${raw}")`);
  }
  const value = Number.parseFloat(px[1]);
  resolved.set(variable, value);
  return value;
}

/**
 * The touch floor, for hit areas drawn on the canvas.
 *
 * A function rather than a constant, for the same timing reason as the colours. In the document it
 * is the `touch` spacing token — `min-h-touch` — so both sides read one number.
 */
export const touchMin = (): number => readSpacing("touch");

/* ── Colour maths ──────────────────────────────────────────────────────────────────────────── */

/** `0xRRGGBB` → `#rrggbb`. */
export const css = (color: number): string => `#${color.toString(16).padStart(6, "0")}`;

/** Mixes two colours. Used for gradients and pressed states without new tokens. */
export const mix = (a: number, b: number, t: number): number => {
  const channel = (shift: number) => {
    const x = (a >> shift) & 0xff;
    const y = (b >> shift) & 0xff;
    return Math.round(x + (y - x) * t) & 0xff;
  };
  return (channel(16) << 16) | (channel(8) << 8) | channel(0);
};

/** A lighter and a darker relative of a colour, for two-stop gradients. */
export const lighten = (color: number, t = 0.28): number => mix(color, 0xffffff, t);
export const darken = (color: number, t = 0.22): number => mix(color, 0x000000, t);


/* ── Family identity, as a number ──────────────────────────────────────────────────────────── */

/**
 * The accent a family wears, resolved to a number for the canvas.
 *
 * The table itself is in `accent.ts` and holds NAMES — one table, two readings. The document gets
 * static Tailwind classes from it; Pixi gets this.
 */
export const familyColor = (family: string): number => COLOR[familyLook(family).accent];
